import { modalManager } from '../UI/modalManager.js';
import { langManager } from '../lang/langManager.js';
// CRITICAL : Import calculator to ensure it loads on SPA navigation
import './pageCateringCostCalc.js';
import '/css/catering.css';

/************************************** */
/* PAGE CONFIGURATION                   */

const scenePageMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games',
  boutique: '/boutique'
};

const pageConfig = {
  initialScene: 'catering',
  navigationMode: 'spa',
  history: true,
  scenePageMap,
  prefetchScenes: ['home', 'market', 'about', 'games', 'boutique']
};

if (!window.__BUBAR_PAGE__) {
  window.__BUBAR_PAGE__ = pageConfig;
}
import('../core/page.js').catch(err => console.error('Failed to load page runtime', err));

/*********************************************
 * Catering Modals
 *********************************************/

let turnstileWidgetId = null;
let turnstileLoading = false;

// Helper function to render translations
// Register gallery modal
modalManager.registerModal('catering', 'gallery-section', {
  initialize: modalElement => {
    const galleryImages = modalElement.querySelectorAll('.gallery-item img');
    const overlay = document.getElementById('imageOverlay');

    setupGalleryLightbox(galleryImages, overlay);
    const touchCleanup = setupTouchSwipe(overlay, galleryImages);

    return touchCleanup;
  }
});

// Register menu modal
modalManager.registerModal('catering', 'menu-section', {
  initialize: modalElement => {
    renderTranslations();
  },

  updateContent: () => {
    renderTranslations();
  }
});

// Register cost calculator modal
modalManager.registerModal('catering', 'costs-section', {
  initialize: modalElement => {
    renderTranslations();
    initializeTurnstile();
  },

  openHandler: () => {
    // Just recalculate if already initialized, don't re-init
    if (window.__calculatorDebug?.isInitialized()) {
      window.dispatchEvent(new CustomEvent('recalculate-costs'));
    }
    return true;
  }
});

// Helper functions
function renderTranslations() {
  // Handle lists (not covered by langManager)
  document.querySelectorAll('[data-i18n-list]').forEach(el => {
    const key = el.getAttribute('data-i18n-list');
    const items = langManager.get(key);
    if (Array.isArray(items)) {
      el.innerHTML = items.map(item => `<li>${item}</li>`).join('');
    }
  });

  langManager.updatePageText();
}

// Gallery lightbox setup
function setupGalleryLightbox(images, overlay) {
  let currentIndex = 0;
  const overlayImg = overlay?.querySelector('img');

  images.forEach((img, idx) => {
    img.addEventListener('click', () => {
      currentIndex = idx;
      if (overlayImg) {
        overlayImg.src = images[currentIndex].src;
        overlay.classList.add('open');
      }
    });
  });

  overlay?.addEventListener('click', () => {
    overlay.classList.remove('open');
  });

  // Keyboard navigation
  const handleKeyboard = e => {
    if (!overlay?.classList.contains('open')) return;

    if (e.key === 'ArrowRight') {
      currentIndex = (currentIndex + 1) % images.length;
      if (overlayImg) overlayImg.src = images[currentIndex].src;
    } else if (e.key === 'ArrowLeft') {
      currentIndex = (currentIndex - 1 + images.length) % images.length;
      if (overlayImg) overlayImg.src = images[currentIndex].src;
    }
  };

  document.addEventListener('keydown', handleKeyboard);
}

function setupTouchSwipe(overlay, images) {
  let startX = 0;
  let currentIndex = 0;
  const overlayImg = overlay?.querySelector('img');

  const handleTouchStart = e => {
    startX = e.touches[0].clientX;
  };

  const handleTouchEnd = e => {
    const endX = e.changedTouches[0].clientX;
    if (Math.abs(endX - startX) > 50) {
      if (endX < startX) {
        currentIndex = (currentIndex + 1) % images.length;
      } else {
        currentIndex = (currentIndex - 1 + images.length) % images.length;
      }
      if (overlayImg) overlayImg.src = images[currentIndex].src;
    }
  };

  overlay?.addEventListener('touchstart', handleTouchStart);
  overlay?.addEventListener('touchend', handleTouchEnd);

  return () => {
    overlay?.removeEventListener('touchstart', handleTouchStart);
    overlay?.removeEventListener('touchend', handleTouchEnd);
  };
}

function initializeTurnstile() {
  // If this page is embedded in a parent frame that is sandboxed
  // without `allow-scripts`, Turnstile will create about:blank frames
  // and script execution will be blocked. Detect that situation and
  // skip initialization to avoid noisy errors in the console.
  try {
    const inIframe = window.top !== window.self;
    const frameEl = window.frameElement;
    if (inIframe && frameEl && frameEl.hasAttribute && frameEl.hasAttribute('sandbox')) {
      const sandboxAttr = frameEl.getAttribute('sandbox') || '';
      if (!sandboxAttr.includes('allow-scripts')) {
        console.warn(
          '[Catering] Parent frame sandboxed without allow-scripts — skipping Turnstile init'
        );
        return;
      }
    }
  } catch (err) {
    // Accessing frame information may fail in cross-origin scenarios; ignore safely
  }

  const container = document.getElementById('turnstile-container');
  if (!container) {
    console.warn('[Catering] Turnstile container not found');
    return;
  }

  const sitekey = container.dataset.sitekey;
  const theme = container.dataset.theme || 'light';
  if (!sitekey) {
    console.warn('[Catering] Turnstile sitekey missing on container');
    return;
  }

  const renderWidget = () => {
    if (!window.turnstile) return;
    if (turnstileWidgetId !== null) {
      try {
        window.turnstile.reset(turnstileWidgetId);
      } catch (err) {
        console.warn('[Catering] Turnstile reset failed', err);
      }
      return;
    }
    turnstileWidgetId = window.turnstile.render(container, {
      sitekey,
      theme,
      callback: window.onTurnstileSuccess,
      'expired-callback': () => {
        const tokenField = document.getElementById('turnstile_token');
        if (tokenField) tokenField.value = '';
      }
    });
  };

  if (window.turnstile) {
    renderWidget();
    return;
  }

  if (!turnstileLoading) {
    turnstileLoading = true;
    const scriptId = 'cf-turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        turnstileLoading = false;
        renderWidget();
      };
      script.onerror = () => {
        turnstileLoading = false;
        console.warn('[Catering] Failed to load Turnstile script');
      };
      document.head.appendChild(script);
    }
  }
}

// Export functions
export function openGallery() {
  return modalManager.openModal('catering', 'gallery-section');
}

export function openMenu() {
  return modalManager.openModal('catering', 'menu-section');
}

export function openCosts() {
  return modalManager.openModal('catering', 'costs-section');
}
